// src/components/Notifications.js
import React, { useEffect, useState } from 'react';
import { connectWebSocket } from '../services/ws'; // WebSockets thuần
import { Link } from 'react-router-dom';

// CSS cho animation
const styles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

/**
 * Component hiển thị thông báo dạng toast
 * Hiển thị thông báo trong 5 giây và có thể nhấp vào để chuyển hướng
 */
const Notifications = () => {
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    console.log('[Notifications] Component mounted');
    
    // Lấy thông tin người dùng từ localStorage
    const userString = localStorage.getItem('user');
    console.log('[Notifications] User from localStorage:', userString);
    
    const user = userString ? JSON.parse(userString) : { id: 'ANONYMOUS', role: 'PUBLIC_ROOM' };
    console.log('[Notifications] Parsed user:', user);
    
    // Lắng nghe sự kiện notification_received từ window
    const handleNotificationEvent = (event) => {
      console.log('[Notifications] Received notification_received event:', event.detail);
      
      if (event.detail && event.detail.notification) {
        const notification = event.detail.notification;
        
        // Thêm thông báo mới vào danh sách
        const newNotification = {
          id: notification._id || Date.now(),
          message: notification.message,
          title: notification.title || '',
          type: notification.type || notification.notificationType || 'INFO',
          icon: notification.icon || 'fas fa-bell',
          link: notification.link || '#',
          priority: notification.priority || 'normal',
          data: notification.data || {},
          timestamp: new Date()
        };
        
        console.log('[Notifications] Created new notification object:', newNotification);
        
        setNotifications(prev => [...prev, newNotification]);
        
        // Tự xoá sau 5 giây
        setTimeout(() => {
          setNotifications(prev => prev.filter(n => n.id !== newNotification.id));
          console.log('[Notifications] Removed notification after timeout:', newNotification.id);
        }, 5000);
      }
    };
    
    // Đăng ký lắng nghe sự kiện
    window.addEventListener('notification_received', handleNotificationEvent);
    console.log('[Notifications] Added notification_received event listener');
    
    // Kết nối WebSocket với ID và vai trò của người dùng
    console.log('[Notifications] Connecting to WebSocket with:', { 
      userId: user.id || user._id || 'ANONYMOUS', 
      role: user.role || 'PUBLIC_ROOM' 
    });
    
    connectWebSocket({ 
      userId: user.id || user._id || 'ANONYMOUS', 
      role: user.role || 'PUBLIC_ROOM' 
    }, (data) => {
      console.log('[Notifications] Received data from WebSocket:', data);
      
      if (data.type === 'notification') {
        console.log('[Notifications] Processing notification from WebSocket');
        
        // Thêm thông báo mới vào danh sách
        const newNotification = {
          id: data._id || Date.now(),
          message: data.message,
          title: data.title || '',
          type: data.notificationType || data.type || 'INFO',
          icon: data.icon || 'fas fa-bell',
          link: data.link || '#',
          priority: data.priority || 'normal',
          data: data.data || {},
          timestamp: new Date()
        };
        
        console.log('[Notifications] Created new notification object from WebSocket:', newNotification);
        
        setNotifications(prev => [...prev, newNotification]);
        
        // Tự xoá sau 5 giây
        setTimeout(() => {
          setNotifications(prev => prev.filter(n => n.id !== newNotification.id));
          console.log('[Notifications] Removed WebSocket notification after timeout:', newNotification.id);
        }, 5000);
      }
    });
    
    // Cleanup function
    return () => {
      window.removeEventListener('notification_received', handleNotificationEvent);
      console.log('[Notifications] Removed notification_received event listener');
    };
  }, []);

  // Log khi notifications thay đổi
  useEffect(() => {
    console.log('[Notifications] Notifications state updated:', notifications);
  }, [notifications]);

  // Xác định loại thông báo để hiển thị màu sắc phù hợp
  const getAlertClass = (type) => {
    switch (type) {
      case 'EVALUATION':
        return 'alert-primary';
      case 'APPROVAL':
        return 'alert-success';
      case 'TASK':
      case 'ASSIGNMENT':
        return 'alert-warning';
      case 'DRIVER_ASSIGNMENT':
        return 'alert-primary';
      case 'SCHEDULE':
        return 'alert-info';
      case 'INCIDENT':
        return 'alert-danger';
      case 'REQUEST':
      case 'LEAVE_REQUEST':
      case 'FUEL_REQUEST':
        return 'alert-info';
      case 'VEHICLE_EXPIRED':
      case 'VEHICLE_EXPIRING':
      case 'VEHICLE_MAINTENANCE':
      case 'VEHICLE_INSURANCE':
      case 'VEHICLE_FUEL':
        return 'alert-warning';
      case 'REPORT':
        return 'alert-primary';
      case 'COMMENT':
      case 'COLLABORATION':
        return 'alert-light';
      case 'SYSTEM':
        return 'alert-secondary';
      case 'ANNOUNCEMENT':
        return 'alert-info';
      case 'DOCUMENT':
        return 'alert-light';
      case 'INFO':
        return 'alert-info';
      default:
        return 'alert-info';
    }
  };
  
  // Xác định icon phù hợp với loại thông báo
  const getNotificationIcon = (type, defaultIcon) => {
    if (defaultIcon && defaultIcon !== 'fas fa-bell') {
      return defaultIcon;
    }
    
    switch (type) {
      case 'EVALUATION':
        return 'fas fa-clipboard-check';
      case 'APPROVAL':
        return 'fas fa-check-circle';
      case 'TASK':
      case 'ASSIGNMENT':
        return 'fas fa-tasks';
      case 'DRIVER_ASSIGNMENT':
        return 'fas fa-id-card';
      case 'SCHEDULE':
        return 'fas fa-calendar-week';
      case 'INCIDENT':
        return 'fas fa-exclamation-triangle';
      case 'REQUEST':
        return 'fas fa-car';
      case 'LEAVE_REQUEST':
        return 'fas fa-calendar-alt';
      case 'FUEL_REQUEST':
        return 'fas fa-gas-pump';
      case 'VEHICLE_EXPIRED':
      case 'VEHICLE_EXPIRING':
        return 'fas fa-exclamation-circle';
      case 'VEHICLE_MAINTENANCE':
        return 'fas fa-wrench';
      case 'VEHICLE_INSURANCE':
        return 'fas fa-shield-alt';
      case 'VEHICLE_FUEL':
        return 'fas fa-gas-pump';
      case 'REPORT':
        return 'fas fa-file-alt';
      case 'COMMENT':
        return 'fas fa-comment';
      case 'COLLABORATION':
        return 'fas fa-users';
      case 'SYSTEM':
        return 'fas fa-cog';
      case 'ANNOUNCEMENT':
        return 'fas fa-bullhorn';
      case 'DOCUMENT':
        return 'fas fa-file';
      case 'INFO':
        return 'fas fa-info-circle';
      default:
        return 'fas fa-bell';
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div
        className="notification-container"
        style={{ 
          position: 'fixed', 
          top: '70px', 
          right: '20px', 
          zIndex: 9999,
          maxWidth: '350px'
        }}
      >
        {notifications.map((notification) => (
          <Link 
            to={notification.link} 
            key={notification.id}
            style={{ textDecoration: 'none' }}
          >
            <div
              className={`alert ${getAlertClass(notification.type)} d-flex align-items-center`}
              style={{ 
                marginBottom: '10px',
                boxShadow: '0 4px 8px rgba(17, 24, 39,0.1)',
                cursor: 'pointer',
                animation: 'fadeIn 0.3s'
              }}
            >
              <i className={`${getNotificationIcon(notification.type, notification.icon)} me-2`}></i>
              <div>
                {notification.title && (
                  <div className="fw-bold">{notification.title}</div>
                )}
                <div>{notification.message}</div>
                <div className="text-muted small mt-1">
                  {notification.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </>
  );
};

export default Notifications;
